import React, { useRef, useState, useCallback, useEffect } from 'react';
import Webcam from 'react-webcam';

/**
 * 에스테틱 Before/After 사진 촬영용 가이드 카메라 컴포넌트
 *
 * @param {Function} onCapture - 촬영 완료 시 호출되는 콜백 (imageSrc: base64 string)
 * @param {Function} onClose - 닫기 버튼 클릭 시 호출되는 콜백
 */
const GuideCamera = ({ onCapture, onClose }) => {
  const webcamRef = useRef(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const [guideSize, setGuideSize] = useState({ width: 280, height: 360 });

  // 가이드라인 크기를 뷰포트에 맞게 조정
  useEffect(() => {
    const updateGuideSize = () => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      // 화면 크기에 따라 가이드 크기 계산 (화면의 70%)
      const baseSize = Math.min(vw * 0.7, vh * 0.5);

      setGuideSize({
        width: Math.min(280, baseSize),
        height: Math.min(360, baseSize * 1.3)
      });
    };

    updateGuideSize();
    window.addEventListener('resize', updateGuideSize);

    return () => window.removeEventListener('resize', updateGuideSize);
  }, []);

  // 모바일 후면 카메라 설정 (3:4 세로 비율)
  const videoConstraints = {
    width: { ideal: 1080 },
    height: { ideal: 1440 },
    aspectRatio: 0.75, // 3:4 비율
    facingMode: { exact: "environment" } // 후면 카메라 강제
  };

  // 촬영 버튼 클릭
  const handleCapture = useCallback(() => {
    const imageSrc = webcamRef.current.getScreenshot();
    setCapturedImage(imageSrc);
  }, [webcamRef]);

  // 다시 찍기
  const handleRetake = () => {
    setCapturedImage(null);
  };

  // 사용하기 (부모 컴포넌트로 전달)
  const handleConfirm = () => {
    if (capturedImage && onCapture) {
      onCapture(capturedImage);
    }
  };

  // 카메라 에러 처리
  const handleUserMediaError = (error) => {
    console.error("Camera error:", error);
    setCameraError("카메라를 사용할 수 없습니다. 권한을 확인해주세요.");
  };

  return (
    <div className="fixed inset-0 z-50 bg-black">
      {/* 닫기 버튼 */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-50 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-70"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* 카메라 에러 메시지 */}
      {cameraError && (
        <div className="absolute top-20 left-1/2 transform -translate-x-1/2 z-50 bg-red-500 text-white px-4 py-2 rounded-lg">
          {cameraError}
        </div>
      )}

      {/* 촬영 완료 - 미리보기 화면 */}
      {capturedImage ? (
        <div className="relative w-full h-full flex flex-col items-center justify-center">
          <img
            src={capturedImage}
            alt="Captured"
            className="max-w-full max-h-full object-contain"
          />

          {/* 미리보기 하단 버튼 */}
          <div className="absolute bottom-8 left-0 right-0 flex justify-center gap-4 px-4">
            <button
              onClick={handleRetake}
              className="flex-1 max-w-xs bg-gray-600 text-white py-3 px-6 rounded-full text-lg font-semibold hover:bg-gray-700 transition"
            >
              다시 찍기
            </button>
            <button
              onClick={handleConfirm}
              className="flex-1 max-w-xs bg-blue-600 text-white py-3 px-6 rounded-full text-lg font-semibold hover:bg-blue-700 transition"
            >
              사용하기
            </button>
          </div>
        </div>
      ) : (
        // 카메라 화면
        <div className="relative w-full h-full flex items-center justify-center">
          <Webcam
            ref={webcamRef}
            audio={false}
            screenshotFormat="image/jpeg"
            videoConstraints={videoConstraints}
            onUserMediaError={handleUserMediaError}
            className="w-full h-full object-cover"
          />

          {/* 가이드라인 오버레이 */}
          <div className="absolute inset-0 pointer-events-none">
            {/* 중앙 타원 가이드 (얼굴 실루엣) - 반응형 크기 */}
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <div
                className="border-4 border-white border-opacity-60"
                style={{
                  width: `${guideSize.width}px`,
                  height: `${guideSize.height}px`,
                  borderRadius: '50% 50% 50% 50% / 60% 60% 40% 40%', // 얼굴형 타원
                  boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.4)' // 바깥 어둡게
                }}
              />
            </div>

            {/* 십자선 (Crosshair) */}
            <div className="absolute top-1/2 left-0 right-0 h-px bg-white opacity-40" />
            <div className="absolute top-0 bottom-0 left-1/2 w-px bg-white opacity-40" />

            {/* 상단 안내 텍스트 */}
            <div className="absolute top-12 left-0 right-0 text-center">
              <p className="text-white text-lg font-semibold drop-shadow-lg">
                얼굴을 가이드 라인에 맞춰주세요
              </p>
            </div>
          </div>

          {/* 촬영 버튼 - 터치 최적화 */}
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
            <button
              onClick={handleCapture}
              className="w-16 h-16 md:w-20 md:h-20 bg-white rounded-full border-4 border-gray-300 hover:bg-gray-100 active:scale-95 transition-transform shadow-lg"
              style={{ touchAction: 'manipulation' }}
            >
              <div className="w-full h-full rounded-full border-2 border-gray-400" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GuideCamera;
